<script setup>
import { computed, nextTick, onUnmounted, ref } from 'vue'
import { api } from '../services/api'
import { store } from '../store'
import PronunciationButton from './PronunciationButton.vue'
import { stopPronunciation } from '../services/pronunciation'

const emit = defineEmits(['saved'])
const dialog = ref(null)
const entry = ref(null)
const term = ref('')
const lessonId = ref(null)
const lessonTitle = ref('')
const context = ref('')
const translation = ref('')
const note = ref('')
const senseIndex = ref(0)
const busy = ref(false)
const saving = ref(false)
const saved = ref(false)
const error = ref('')
const sense = computed(() => entry.value?.meanings?.[senseIndex.value] || null)
let sequence = 0
let controller = null

function setEntry(value, existing) {
  entry.value = existing?.entry || value
  translation.value = entry.value.translation || ''
  note.value = existing?.note || ''
  saved.value = !!existing
  senseIndex.value = 0
  if (existing?.context) context.value = existing.context
}
async function open(options) {
  controller?.abort()
  stopPronunciation()
  const current = ++sequence
  term.value = options.term
  lessonId.value = options.lessonId
  lessonTitle.value = options.lessonTitle || ''
  context.value = (options.context || '').slice(0, 1000)
  entry.value = null
  error.value = ''
  saved.value = false
  saving.value = false
  await nextTick()
  if (sequence !== current) return
  if (!dialog.value.open) dialog.value.showModal()
  if (options.saved) { busy.value = false; setEntry(options.saved.entry, options.saved); return }
  busy.value = true
  controller = new AbortController()
  try {
    const data = await api(`/dictionary?${new URLSearchParams({term: options.term, lesson_id: options.lessonId})}`, {signal: controller.signal})
    if (sequence === current) setEntry(data.entry, data.saved)
  } catch (err) {
    if (sequence === current && err.name !== 'AbortError') error.value = err.message
  } finally { if (sequence === current) busy.value = false }
}
function close() { dialog.value?.close() }
let pressedOutside = false
function isOutside(event) {
  if (event.target !== dialog.value) return false
  const rect = dialog.value.getBoundingClientRect()
  return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom
}
function onBackdropDown(event) { pressedOutside = isOutside(event) }
function onBackdropClick(event) { if (pressedOutside && isOutside(event)) close(); pressedOutside = false }
function cleanup() { sequence++; controller?.abort(); stopPronunciation(); entry.value = null; busy.value = false }
async function save() {
  if (saving.value || !entry.value) return
  saving.value = true
  error.value = ''
  const current = sequence
  const value = {...entry.value, translation: translation.value.trim(), meanings: sense.value ? [sense.value] : []}
  try {
    const data = await api('/vocabulary', {method: 'POST', body: JSON.stringify({term: entry.value.term, lesson_id: lessonId.value, entry: value, context: context.value, note: note.value})})
    if (current === sequence) { saved.value = true; emit('saved', data.item) }
  } catch (err) { if (current === sequence) error.value = err.message }
  finally { if (current === sequence) saving.value = false }
}
function lookupSynonym(word) { open({term: word, lessonId: lessonId.value, lessonTitle: lessonTitle.value, context: context.value}) }
defineExpose({open, close})
onUnmounted(cleanup)
</script>

<template>
  <Teleport to="body">
    <dialog ref="dialog" class="vocabulary-dialog" aria-labelledby="vocabulary-dialog-title" @close="cleanup" @pointerdown="onBackdropDown" @click="onBackdropClick">
      <header class="dictionary-heading">
        <div><span class="dictionary-eyebrow">TRA TỪ TIẾNG ANH</span><h2 id="vocabulary-dialog-title" lang="en">{{ entry?.headword || term }}</h2></div>
        <button type="button" class="dictionary-close" aria-label="Đóng tra từ" @click="close">×</button>
      </header>
      <p v-if="entry && entry.headword !== entry.term" class="dictionary-muted">Từ trong bài: <b lang="en">{{ entry.term }}</b> · Đang tra dạng gốc.</p>
      <div v-if="busy" class="dictionary-loading" role="status"><div class="loader"></div>Đang tra nghĩa và phiên âm…</div>
      <p v-if="error" class="dictionary-error" role="alert">{{ error }}</p>
      <template v-if="entry">
        <div class="dictionary-pronunciation">
          <div><span v-for="(phonetic, index) in entry.phonetics" :key="index" class="dictionary-ipa">{{ phonetic.ipa }} <small>{{ phonetic.accent }}</small></span><span v-if="!entry.phonetics.some(item => item.ipa)" class="dictionary-muted">Nguồn tra chưa có phiên âm IPA.</span></div>
          <PronunciationButton :key="entry.term" :term="entry.headword" :phonetics="entry.phonetics" />
        </div>
        <p class="dictionary-muted audio-source">{{ entry.phonetics.some(item => item.audio) ? 'Nghe bản thu; dùng giọng thiết bị nếu bản thu không phát được.' : 'Phát âm bằng giọng tiếng Anh của thiết bị.' }}</p>
        <label v-if="entry.meanings.length > 1" class="dictionary-field">Chọn nghĩa phù hợp với câu<select v-model="senseIndex" class="form-control"><option v-for="(meaning, index) in entry.meanings" :key="index" :value="index">{{ meaning.part_of_speech }} · {{ meaning.definition }}</option></select></label>
        <div class="dictionary-definition" v-if="sense">
          <span class="dictionary-pos">{{ sense.part_of_speech }}</span>
          <p v-if="sense.definition" lang="en">{{ sense.definition }}</p>
          <blockquote v-if="sense.example" lang="en">{{ sense.example }}</blockquote>
        </div>
        <label class="dictionary-field">Nghĩa tiếng Việt<textarea v-model="translation" class="form-control" rows="2" maxlength="1500" placeholder="Bổ sung hoặc chỉnh nghĩa phù hợp với câu" /></label>
        <p v-if="entry.translation_source" class="dictionary-muted">{{ entry.translation_source }}{{ entry.meanings.length > 1 ? ' · Đối chiếu với nghĩa em chọn và câu trong bài.' : '' }}</p>
        <div class="dictionary-synonyms"><h3>Từ / cụm đồng nghĩa</h3><div v-if="sense?.synonyms?.length" class="synonym-chips"><button v-for="word in sense.synonyms" :key="word" type="button" lang="en" @click="lookupSynonym(word)">{{ word }}</button></div><p v-else class="dictionary-muted">Nguồn tra chưa có từ đồng nghĩa cho nghĩa này.</p></div>
        <div v-if="context" class="dictionary-context"><h3>Câu trong bài</h3><p>{{ context }}</p></div>
        <p v-for="notice in entry.notices" :key="notice" class="dictionary-notice" role="status">{{ notice }}</p>
        <label v-if="store.user?.role === 'student'" class="dictionary-field">Ghi chú của em <span>(không bắt buộc)</span><textarea v-model="note" class="form-control" rows="2" maxlength="2000" placeholder="Mẹo nhớ, ví dụ riêng hoặc nghĩa em muốn ghi nhớ…" /></label>
        <div class="dictionary-sources"><template v-for="(source, index) in entry.sources" :key="index"><a v-if="source.url" :href="source.url" target="_blank" rel="noopener noreferrer">{{ source.title }}</a><span v-else>{{ source.title }}</span></template></div>
        <footer class="dictionary-footer">
          <div><span class="dictionary-muted">Lưu theo bài</span><strong>{{ lessonTitle || 'Bài Tiếng Anh đang học' }}</strong></div>
          <button v-if="store.user?.role === 'student'" type="button" class="btn btn-primary" :disabled="saving || (!translation.trim() && !sense?.definition)" @click="save">{{ saving ? 'Đang lưu…' : saved ? '✓ Đã lưu · Cập nhật' : '+ Lưu từ' }}</button>
        </footer>
        <p v-if="saved" class="dictionary-saved" role="status">Đã lưu vào kho từ vựng của em. <RouterLink :to="{path:'/tu-vung', query:{lesson_id:lessonId}}" @click="close">Xem từ của bài này →</RouterLink></p>
      </template>
    </dialog>
  </Teleport>
</template>

<style scoped>
.vocabulary-dialog { width: min(540px, calc(100vw - 28px)); max-height: calc(100dvh - 36px); margin: auto; padding: 25px; border: 1px solid #dedaf5; border-radius: 22px; color: #283150; background: #fff; box-shadow: 0 24px 90px #25234238; overflow-y: auto; overscroll-behavior: contain; }
.vocabulary-dialog::backdrop { background: #20204470; backdrop-filter: blur(3px); }
.dictionary-heading { display: flex; justify-content: space-between; gap: 16px; align-items: start; }
.dictionary-eyebrow { color: #766bad; font-size: 10px; font-weight: 800; letter-spacing: 1.3px; }
h2 { margin: 5px 0 0; font-size: 30px; overflow-wrap: anywhere; }
.dictionary-close { flex-shrink: 0; width: 35px; height: 35px; border: 0; border-radius: 50%; font-size: 25px; color: #595273; background: #f3f1fb; cursor: pointer; }
.dictionary-loading { display: flex; align-items: center; gap: 14px; padding: 30px 0; font-size: 14px; }
.dictionary-loading .loader { width: 24px; height: 24px; margin: 0; }
.dictionary-pronunciation { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; margin-top: 15px; }
.dictionary-ipa { display: block; margin: 4px 0; font-size: 16px; color: #645692; }
.dictionary-ipa small { font-size: 11px; color: #81869b; }
.dictionary-muted { color: #788099; font-size: 12px; line-height: 1.6; }
.audio-source { margin: 9px 0 18px; }
.dictionary-field { display: grid; gap: 8px; margin: 18px 0 5px; font-size: 13px; font-weight: 700; }
.dictionary-field span { font-weight: 400; color: #788099; }
.dictionary-field textarea { min-height: 70px; font-weight: 400; }
.dictionary-pos { display: inline-block; padding: 4px 9px; border-radius: 6px; color: #655b96; background: #f0ecfa; font-size: 12px; }
.dictionary-definition p, .dictionary-context p { font-size: 14px; line-height: 1.75; overflow-wrap: anywhere; }
.dictionary-definition blockquote { margin: 12px 0; border-left: 3px solid #c9c0e6; padding-left: 12px; color: #737991; font-size: 13px; line-height: 1.6; }
h3 { margin: 0 0 10px; font-size: 13px; }
.dictionary-synonyms { margin-top: 18px; }
.synonym-chips { display: flex; gap: 7px; flex-wrap: wrap; }
.synonym-chips button { padding: 6px 10px; border: 1px solid #e4dff2; border-radius: 20px; background: #faf8ff; color: #68509a; font-size: 12px; cursor: pointer; }
.dictionary-context { margin-top: 18px; padding: 14px; border-radius: 12px; background: #faf9fd; }
.dictionary-context p { margin: 0; }
.dictionary-notice { font-size: 12px; color: #8a673c; line-height: 1.6; }
.dictionary-error { padding: 12px; border-radius: 10px; font-size: 13px; color: #9d4639; background: #fff2ec; }
.dictionary-sources { display: flex; gap: 6px 12px; flex-wrap: wrap; margin: 16px 0; font-size: 10px; color: #81869b; }
.dictionary-sources a { color: #7569a0; }
.dictionary-footer { display: flex; align-items: center; justify-content: space-between; gap: 16px; border-top: 1px solid #eeeaf5; padding-top: 17px; }
.dictionary-footer strong { display: block; font-size: 12px; line-height: 1.6; }
.dictionary-footer .btn { flex-shrink: 0; }
.dictionary-saved { color: #447b56; font-size: 12px; line-height: 1.7; margin-bottom: 0; }
.dictionary-saved a { color: #6052a1; }
@media(max-width: 560px) { .vocabulary-dialog { padding: 20px; } .dictionary-footer { align-items: stretch; flex-direction: column; gap: 12px; } .dictionary-footer .btn { width: 100%; } }
</style>
